// blog.ts — DTOs for the blog/use-cases cycle (docs/superpowers/specs/
// 2026-08-06-blog-seo-design.md §2). Public shapes (`Public*Dto`) back `/v1/blog/*` (no
// auth, cacheable); admin shapes (`Admin*Dto`) back `/api/admin/posts`+`/api/admin/authors`
// behind `requireAdmin`. Backed by `createPostsRepo`/`createAuthorsRepo` (`@repo/db`).
import type { PostStatus } from './enums.ts';

/** The byline as it appears embedded on a post (list or detail) — never a standalone public
 * endpoint (spec §1: "Bylines are real people"; there's no `/v1/blog/authors`). */
export interface AuthorSummaryDto {
  name: string;
  slug: string;
  role: string | null;
  avatarUrl: string | null;
}

/** `GET /v1/blog/posts` item (spec §2) — no `bodyMd`; the list view only ever needs the
 * excerpt. */
export interface PublicPostListItemDto {
  slug: string;
  title: string;
  excerpt: string;
  coverUrl: string | null;
  tags: string[];
  publishedAt: string;
  author: AuthorSummaryDto;
}

/** `GET /v1/blog/posts?tag=&page=` response — page-NUMBER pagination like the admin lists
 * (`AdminPageMeta`), not the customer-facing cursor style: a blog's "page 2 of /blog" is a
 * meaningful, linkable, repeatable request, unlike an infinite-scroll feed. `perPage` is
 * always 12 (`PUBLISHED_PAGE_SIZE`, `@repo/db`), included on the wire rather than hardcoded
 * client-side. */
export interface PublicPostListDto {
  items: PublicPostListItemDto[];
  page: number;
  perPage: number;
  totalCount: number;
  totalPages: number;
}

/** `GET /v1/blog/posts/:slug` response — the full post including raw `bodyMd` (spec §2), fed
 * to `@repo/markdown`'s `renderMarkdown` for SSR. 404 (not this shape) for a draft/unknown
 * slug — see `PostsRepo.getPublishedBySlug`'s doc comment on why those two cases can't be
 * told apart on the wire. */
export interface PublicPostDetailDto {
  slug: string;
  title: string;
  excerpt: string;
  bodyMd: string;
  coverUrl: string | null;
  tags: string[];
  publishedAt: string;
  author: AuthorSummaryDto;
}

/** `GET /v1/blog/tags` response item — distinct tags across PUBLISHED posts only, with
 * counts (spec §2). */
export interface PublicTagDto {
  tag: string;
  count: number;
}

/** `GET/POST /api/admin/posts`, `GET/PATCH /api/admin/posts/:id` row/detail — the admin CMS
 * sees every field regardless of status, plus the raw `authorId` (the post editor's author
 * <select> needs the id, not just the embedded summary). */
export interface AdminPostDto {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  bodyMd: string;
  coverUrl: string | null;
  tags: string[];
  status: PostStatus;
  publishedAt: string | null;
  authorId: string;
  author: AuthorSummaryDto;
  createdAt: string;
  updatedAt: string;
}

/**
 * What a post looks like to the outside world, derived from BOTH columns that decide it.
 *
 * Scheduling adds no third status and no job that flips one: a post is visible when it is
 * `published` AND its `publishedAt` has arrived, so "schedule for Tuesday" is just
 * `status: 'published'` with a future `publishedAt`. The public queries in
 * `packages/db/src/repos/posts.ts` enforce exactly that with `publishedAt <= now()`.
 *
 * The consequence is that `status` alone is a lie in the admin list — a scheduled post reads
 * as `published` while no visitor can reach it. Anything that shows a post's state to a human
 * must call this instead of reading `status`.
 */
export type PostVisibility = 'draft' | 'scheduled' | 'live';

export function resolvePostVisibility(
  post: { status: PostStatus; publishedAt: string | Date | null },
  now: Date = new Date(),
): PostVisibility {
  if (post.status !== 'published') return 'draft';
  if (post.publishedAt === null) return 'live'; // published with no date — legacy/imported
  const at = post.publishedAt instanceof Date ? post.publishedAt : new Date(post.publishedAt);
  return at.getTime() > now.getTime() ? 'scheduled' : 'live';
}

/** `POST /api/admin/posts/preview` response (spec §2) — `renderMarkdownToHtml`'s output for
 * whatever `bodyMd` the caller sent, the exact same pipeline `apps/web`'s post page renders
 * through, so preview can never drift from production (`@repo/markdown`'s `pipeline.ts`).
 * Deliberately not tied to a persisted post — see the route's own comment
 * (`apps/api/src/routes/admin-posts.ts`) for why. */
export interface AdminPostPreviewDto {
  html: string;
}

/** `GET/POST/PATCH /api/admin/authors` row (spec §5: the post editor's "author select").
 * `persona` (blog-polish spec §F) is admin-only — a briefing note for the post editor's
 * "Copy AI prompt" button (spec §G). It MUST NEVER be added to `AuthorSummaryDto` above,
 * which is the only shape `/v1/blog/*` embeds. */
export interface AdminAuthorDto {
  id: string;
  slug: string;
  name: string;
  role: string | null;
  bio: string | null;
  avatarUrl: string | null;
  persona: string | null;
}

/** `POST /api/admin/media` response (spec §2) — `CDN_PUBLIC_URL` + the content-addressed
 * `blog/<sha256>.<ext>` key, already joined. */
export interface MediaUploadResponseDto {
  url: string;
}
